import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { FirmAdminLayout } from './FirmAdminLayout';
import {
  Building2,
  Plus,
  Search,
  Filter,
  MoreVertical,
  Mail,
  Phone,
  MapPin,
  Calendar,
  DollarSign,
  FileText,
  Edit,
  Trash2,
  Eye,
  UserCheck,
  UserX,
  Download,
  Upload,
  RefreshCw
} from 'lucide-react';
import { api } from '../auth/authStore';
import { ClientModal, ClientViewModal, ClientImportModal, ClientImportResultsModal } from './ClientModals';

// Types
interface Client {
  id: string;
  displayName: string;
  legalName?: string;
  clientType: 'individual' | 'corporation' | 'partnership' | 'trust' | 'other';
  contact: {
    email?: string;
    phone?: string;
    fax?: string;
    website?: string;
  };
  address: {
    street?: string;
    city?: string;
    province?: string;
    postalCode?: string;
    country?: string;
  };
  taxIds: {
    businessNumber?: string;
    gstNumber?: string;
    qstNumber?: string;
    sin?: string;
  };
  preferredCurrency?: 'CAD' | 'USD';
  notes?: string;
  isActive: boolean;
  fullAddress: string;
  createdAt: string;
  updatedAt: string;
}

interface ClientFormData {
  displayName: string;
  legalName?: string;
  clientType: 'individual' | 'corporation' | 'partnership' | 'trust' | 'other';
  contact: {
    email?: string;
    phone?: string;
    fax?: string;
    website?: string;
  };
  address: {
    street?: string;
    city?: string;
    province?: string;
    postalCode?: string;
    country?: string;
  };
  taxIds: {
    businessNumber?: string;
    gstNumber?: string;
    qstNumber?: string;
    sin?: string;
  };
  preferredCurrency?: 'CAD' | 'USD';
  notes?: string;
  isActive: boolean;
}

interface ClientListResponse {
  clients: Client[];
  total: number;
  page: number;
  limit: number;
}

interface ClientStats {
  totalClients: number;
  activeClients: number;
  inactiveClients: number;
  newClientsThisMonth: number;
}

// API Functions
const clientAPI = {
  // Get all clients with pagination and filtering
  getClients: async (params: {
    page?: number;
    limit?: number;
    search?: string;
    clientType?: string;
    isActive?: boolean;
    sortBy?: string;
    sortOrder?: string;
  } = {}): Promise<ClientListResponse> => {
    const response = await api.get('/firm-admin/clients', { params });
    return response.data.data;
  },

  // Get client statistics
  getClientStats: async (): Promise<ClientStats> => {
    const response = await api.get('/firm-admin/clients/stats');
    return response.data.data;
  },

  // Get client by ID
  getClientById: async (id: string): Promise<Client> => {
    const response = await api.get(`/firm-admin/clients/${id}`);
    return response.data.data;
  },

  // Create new client
  createClient: async (data: ClientFormData): Promise<Client> => {
    const response = await api.post('/firm-admin/clients', data);
    return response.data.data;
  },

  // Update client
  updateClient: async (id: string, data: Partial<ClientFormData>): Promise<Client> => {
    const response = await api.patch(`/firm-admin/clients/${id}`, data);
    return response.data.data;
  },

  // Delete client
  deleteClient: async (id: string): Promise<void> => {
    await api.delete(`/firm-admin/clients/${id}`);
  },

  // Toggle client status
  toggleClientStatus: async (id: string, isActive: boolean): Promise<Client> => {
    const response = await api.patch(`/firm-admin/clients/${id}/status`, { isActive });
    return response.data.data;
  },

  // Search clients
  searchClients: async (query: string): Promise<Client[]> => {
    const response = await api.get('/firm-admin/clients/search', { params: { q: query } });
    return response.data.data;
  },

  // Get active clients
  getActiveClients: async (): Promise<Client[]> => {
    const response = await api.get('/firm-admin/clients/active');
    return response.data.data;
  },

  // Bulk actions
  bulkAction: async (action: string, clientIds: string[]): Promise<any> => {
    const response = await api.post('/firm-admin/clients/bulk-action', { action, clientIds });
    return response.data.data;
  }
};

export const ClientManagement: React.FC = () => {
  const queryClient = useQueryClient();

  // State
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [clientTypeFilter, setClientTypeFilter] = useState<string>('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [sortBy, setSortBy] = useState('displayName');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedClient, setSelectedClient] = useState<Client | null>(null);
  const [selectedClients, setSelectedClients] = useState<string[]>([]);
  const [showBulkActions, setShowBulkActions] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);

  // React Query - Fetch clients
  const {
    data: clientsData,
    isLoading: isLoadingClients,
    error: clientsError,
    refetch: refetchClients
  } = useQuery({
    queryKey: ['clients', {
      page: currentPage,
      limit: pageSize,
      search: searchTerm,
      clientType: clientTypeFilter !== 'all' ? clientTypeFilter : undefined,
      isActive: statusFilter === 'all' ? undefined : statusFilter === 'active',
      sortBy,
      sortOrder
    }],
    queryFn: () => clientAPI.getClients({
      page: currentPage,
      limit: pageSize,
      search: searchTerm || undefined,
      clientType: clientTypeFilter !== 'all' ? clientTypeFilter : undefined,
      isActive: statusFilter === 'all' ? undefined : statusFilter === 'active',
      sortBy,
      sortOrder
    }),
    staleTime: 30000, // 30 seconds
  });

  // React Query - Fetch client statistics
  const {
    data: clientStats,
    isLoading: isLoadingStats
  } = useQuery({
    queryKey: ['client-stats'],
    queryFn: clientAPI.getClientStats,
    staleTime: 60000, // 1 minute
  });

  const clients = clientsData?.clients || [];
  const totalClients = clientsData?.total || 0;

  // Mutations
  const createClientMutation = useMutation({
    mutationFn: clientAPI.createClient,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['clients'] });
      queryClient.invalidateQueries({ queryKey: ['client-stats'] });
      toast.success('Client created successfully');
      setShowAddModal(false);
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to create client');
    },
  });

  const updateClientMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<ClientFormData> }) =>
      clientAPI.updateClient(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['clients'] });
      queryClient.invalidateQueries({ queryKey: ['client-stats'] });
      toast.success('Client updated successfully');
      setShowEditModal(false);
      setSelectedClient(null);
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to update client');
    },
  });

  const deleteClientMutation = useMutation({
    mutationFn: clientAPI.deleteClient,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['clients'] });
      queryClient.invalidateQueries({ queryKey: ['client-stats'] });
      toast.success('Client deleted successfully');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to delete client');
    },
  });

  const toggleStatusMutation = useMutation({
    mutationFn: ({ id, isActive }: { id: string; isActive: boolean }) =>
      clientAPI.toggleClientStatus(id, isActive),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['clients'] });
      queryClient.invalidateQueries({ queryKey: ['client-stats'] });
      toast.success('Client status updated successfully');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to update client status');
    },
  });

  const bulkDeleteMutation = useMutation({
    mutationFn: async (clientIds: string[]) => {
      return clientAPI.bulkAction('delete', clientIds);
    },
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['clients'] });
      queryClient.invalidateQueries({ queryKey: ['client-stats'] });
      setSelectedClients([]);
      toast.success(`${result.successful} clients deleted successfully${result.failed > 0 ? `, ${result.failed} failed` : ''}`);
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to delete clients');
    },
  });

  const bulkStatusMutation = useMutation({
    mutationFn: async ({ clientIds, isActive }: { clientIds: string[]; isActive: boolean }) => {
      return clientAPI.bulkAction(isActive ? 'activate' : 'deactivate', clientIds);
    },
    onSuccess: (result, { isActive }) => {
      queryClient.invalidateQueries({ queryKey: ['clients'] });
      queryClient.invalidateQueries({ queryKey: ['client-stats'] });
      setSelectedClients([]);
      toast.success(`${result.successful} clients ${isActive ? 'activated' : 'deactivated'} successfully${result.failed > 0 ? `, ${result.failed} failed` : ''}`);
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to update client status');
    },
  });

  const [importResults, setImportResults] = useState<{
    successful: any[];
    failed: Array<{ client: ClientFormData; error: string }>;
    total: number;
  } | null>(null);
  const [showImportResultsModal, setShowImportResultsModal] = useState(false);

  const importClientsMutation = useMutation({
    mutationFn: async (clients: ClientFormData[]) => {
      const results = await Promise.allSettled(
        clients.map(client => clientAPI.createClient(client))
      );

      const successful: any[] = [];
      const failed: Array<{ client: ClientFormData; error: string }> = [];

      results.forEach((result, index) => {
        if (result.status === 'fulfilled') {
          successful.push(result.value);
        } else {
          // Extract user-friendly error message
          let errorMessage = 'Unknown error';
          const error = result.reason;

          // Try to extract error message from different response formats
          if (error?.response?.data?.error?.message) {
            // Backend format: { success: false, error: { message: "..." } }
            errorMessage = error.response.data.error.message;
          } else if (error?.response?.data?.message) {
            // Alternative format: { message: "..." }
            errorMessage = error.response.data.message;
          } else if (error?.response?.data?.error) {
            // String error format
            errorMessage = typeof error.response.data.error === 'string'
              ? error.response.data.error
              : 'Unknown error';
          } else if (error?.response?.status) {
            // Fallback: Map status codes to user-friendly messages
            const statusCode = error.response.status;
            switch (statusCode) {
              case 409:
                errorMessage = 'Client already exists (duplicate email or business number)';
                break;
              case 400:
                errorMessage = 'Invalid data provided';
                break;
              case 422:
                errorMessage = 'Missing required information';
                break;
              case 500:
                errorMessage = 'Server error occurred';
                break;
              default:
                errorMessage = `Request failed (Error ${statusCode})`;
            }
          } else if (error?.message) {
            errorMessage = error.message;
          }

          failed.push({
            client: clients[index],
            error: errorMessage,
          });
        }
      });

      return { successful, failed, total: clients.length };
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['clients'] });
      queryClient.invalidateQueries({ queryKey: ['client-stats'] });
      setShowImportModal(false);
      setImportResults(data);
      setShowImportResultsModal(true);
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to import clients');
    },
  });

  // Handler functions
  const handleSearch = (term: string) => {
    setSearchTerm(term);
    setCurrentPage(1); // Reset to first page when searching
  };

  const handleStatusFilter = (status: string) => {
    setStatusFilter(status);
    setCurrentPage(1);
  };

  const handleClientTypeFilter = (type: string) => {
    setClientTypeFilter(type);
    setCurrentPage(1);
  };

  const handleSort = (field: string) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('asc');
    }
    setCurrentPage(1);
  };

  const handleViewClient = (client: Client) => {
    setSelectedClient(client);
    setShowViewModal(true);
  };

  const handleEditClient = (client: Client) => {
    setSelectedClient(client);
    setShowEditModal(true);
  };

  const handleCreateClient = (data: ClientFormData) => {
    createClientMutation.mutate(data);
  };

  const handleUpdateClient = (data: ClientFormData) => {
    if (selectedClient) {
      updateClientMutation.mutate({ id: selectedClient.id, data });
    }
  };

  const handleViewToEdit = () => {
    setShowViewModal(false);
    setShowEditModal(true);
  };

  const handleImportClients = (clients: ClientFormData[]) => {
    importClientsMutation.mutate(clients);
  };

  // Bulk operation handlers
  const handleSelectClient = (clientId: string) => {
    setSelectedClients(prev =>
      prev.includes(clientId)
        ? prev.filter(id => id !== clientId)
        : [...prev, clientId]
    );
  };

  const handleSelectAllClients = () => {
    if (selectedClients.length === clients.length) {
      setSelectedClients([]);
    } else {
      setSelectedClients(clients.map(client => client.id));
    }
  };

  const handleBulkDelete = () => {
    if (selectedClients.length === 0) return;

    const count = selectedClients.length;
    if (window.confirm(`Are you sure you want to delete ${count} client${count > 1 ? 's' : ''}? This action cannot be undone.`)) {
      bulkDeleteMutation.mutate(selectedClients);
    }
  };

  const handleBulkActivate = () => {
    if (selectedClients.length === 0) return;

    const count = selectedClients.length;
    if (window.confirm(`Are you sure you want to activate ${count} client${count > 1 ? 's' : ''}?`)) {
      bulkStatusMutation.mutate({ clientIds: selectedClients, isActive: true });
    }
  };

  const handleBulkDeactivate = () => {
    if (selectedClients.length === 0) return;

    const count = selectedClients.length;
    if (window.confirm(`Are you sure you want to deactivate ${count} client${count > 1 ? 's' : ''}?`)) {
      bulkStatusMutation.mutate({ clientIds: selectedClients, isActive: false });
    }
  };

  const handleBulkExport = () => {
    if (selectedClients.length === 0) return;

    const selectedClientData = clients.filter(client => selectedClients.includes(client.id));
    const csvContent = generateCSV(selectedClientData);
    downloadCSV(csvContent, 'clients-export.csv');
    toast.success(`Exported ${selectedClients.length} clients`);
  };

  const generateCSV = (clientData: Client[]) => {
    const headers = [
      'Display Name',
      'Legal Name',
      'Client Type',
      'Email',
      'Phone',
      'Address',
      'Business Number',
      'GST Number',
      'Status',
      'Created Date'
    ];

    const rows = clientData.map(client => [
      client.displayName,
      client.legalName || '',
      client.clientType,
      client.contact.email || '',
      client.contact.phone || '',
      client.fullAddress,
      client.taxIds.businessNumber || '',
      client.taxIds.gstNumber || '',
      client.isActive ? 'Active' : 'Inactive',
      new Date(client.createdAt).toLocaleDateString()
    ]);

    return [headers, ...rows].map(row =>
      row.map(field => `"${field}"`).join(',')
    ).join('\n');
  };

  const downloadCSV = (content: string, filename: string) => {
    const blob = new Blob([content], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    if (link.download !== undefined) {
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', filename);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const handleDeleteClient = (client: Client) => {
    if (window.confirm(`Are you sure you want to delete ${client.displayName}? This action cannot be undone.`)) {
      deleteClientMutation.mutate(client.id);
    }
  };

  const handleToggleStatus = (client: Client) => {
    const newStatus = !client.isActive;
    const action = newStatus ? 'activate' : 'deactivate';
    if (window.confirm(`Are you sure you want to ${action} ${client.displayName}?`)) {
      toggleStatusMutation.mutate({ id: client.id, isActive: newStatus });
    }
  };

  const getStatusBadge = (isActive: boolean) => {
    return isActive
      ? 'bg-green-100 text-green-800'
      : 'bg-gray-100 text-gray-800';
  };

  const getStatusText = (isActive: boolean) => {
    return isActive ? 'Active' : 'Inactive';
  };

  const getClientTypeLabel = (type: string) => {
    const labels = {
      individual: 'Individual',
      corporation: 'Corporation',
      partnership: 'Partnership',
      trust: 'Trust',
      other: 'Other'
    };
    return labels[type as keyof typeof labels] || type;
  };

  return (
    <FirmAdminLayout>
      <div className="p-6">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Client Management</h1>
          <p className="text-gray-600">Manage your firm's clients and their information</p>
        </div>

        {/* Actions Bar */}
        <div className="flex flex-col sm:flex-row gap-4 mb-6">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              placeholder="Search clients by name, email, or business number..."
              value={searchTerm}
              onChange={(e) => handleSearch(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
          </div>

          <div className="flex gap-2">
            <select
              value={statusFilter}
              onChange={(e) => handleStatusFilter(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            >
              <option value="all">All Status</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>

            <select
              value={clientTypeFilter}
              onChange={(e) => handleClientTypeFilter(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            >
              <option value="all">All Types</option>
              <option value="individual">Individual</option>
              <option value="corporation">Corporation</option>
              <option value="partnership">Partnership</option>
              <option value="trust">Trust</option>
              <option value="other">Other</option>
            </select>

            <button
              onClick={() => refetchClients()}
              disabled={isLoadingClients}
              className="flex items-center px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${isLoadingClients ? 'animate-spin' : ''}`} />
              Refresh
            </button>

            <button
              onClick={() => setShowImportModal(true)}
              className="flex items-center px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              <Upload className="w-4 h-4 mr-2" />
              Import
            </button>

            <button
              onClick={() => setShowAddModal(true)}
              className="flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Client
            </button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white p-4 rounded-lg border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total Clients</p>
                <p className="text-2xl font-bold text-gray-900">
                  {isLoadingStats ? '...' : (clientStats?.totalClients || 0)}
                </p>
              </div>
              <Building2 className="w-8 h-8 text-blue-500" />
            </div>
          </div>

          <div className="bg-white p-4 rounded-lg border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Active Clients</p>
                <p className="text-2xl font-bold text-gray-900">
                  {isLoadingStats ? '...' : (clientStats?.activeClients || 0)}
                </p>
              </div>
              <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                <div className="w-3 h-3 bg-green-500 rounded-full"></div>
              </div>
            </div>
          </div>

          <div className="bg-white p-4 rounded-lg border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Inactive Clients</p>
                <p className="text-2xl font-bold text-gray-900">
                  {isLoadingStats ? '...' : (clientStats?.inactiveClients || 0)}
                </p>
              </div>
              <div className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center">
                <div className="w-3 h-3 bg-gray-500 rounded-full"></div>
              </div>
            </div>
          </div>

          <div className="bg-white p-4 rounded-lg border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">New This Month</p>
                <p className="text-2xl font-bold text-gray-900">
                  {isLoadingStats ? '...' : (clientStats?.newClientsThisMonth || 0)}
                </p>
              </div>
              <FileText className="w-8 h-8 text-purple-500" />
            </div>
          </div>
        </div>

        {/* Bulk Actions Bar */}
        {selectedClients.length > 0 && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <span className="text-sm font-medium text-blue-900">
                  {selectedClients.length} client{selectedClients.length > 1 ? 's' : ''} selected
                </span>
              </div>
              <div className="flex items-center space-x-2">
                <button
                  onClick={handleBulkActivate}
                  disabled={bulkStatusMutation.isPending}
                  className="flex items-center px-3 py-1 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm disabled:opacity-50"
                >
                  <UserCheck className="w-4 h-4 mr-1" />
                  Activate
                </button>
                <button
                  onClick={handleBulkDeactivate}
                  disabled={bulkStatusMutation.isPending}
                  className="flex items-center px-3 py-1 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors text-sm disabled:opacity-50"
                >
                  <UserX className="w-4 h-4 mr-1" />
                  Deactivate
                </button>
                <button
                  onClick={handleBulkExport}
                  className="flex items-center px-3 py-1 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm"
                >
                  <Download className="w-4 h-4 mr-1" />
                  Export
                </button>
                <button
                  onClick={handleBulkDelete}
                  disabled={bulkDeleteMutation.isPending}
                  className="flex items-center px-3 py-1 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors text-sm disabled:opacity-50"
                >
                  <Trash2 className="w-4 h-4 mr-1" />
                  Delete
                </button>
                <button
                  onClick={() => setSelectedClients([])}
                  className="flex items-center px-3 py-1 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors text-sm"
                >
                  Clear
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Clients Table */}
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          {isLoadingClients && (
            <div className="flex items-center justify-center py-12">
              <RefreshCw className="w-8 h-8 text-gray-400 animate-spin" />
              <span className="ml-2 text-gray-600">Loading clients...</span>
            </div>
          )}

          {clientsError && (
            <div className="flex items-center justify-center py-12">
              <div className="text-center">
                <div className="text-red-500 mb-2">Failed to load clients</div>
                <button
                  onClick={() => refetchClients()}
                  className="text-primary-600 hover:text-primary-700"
                >
                  Try again
                </button>
              </div>
            </div>
          )}

          {!isLoadingClients && !clientsError && (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      <input
                        type="checkbox"
                        checked={selectedClients.length === clients.length && clients.length > 0}
                        onChange={handleSelectAllClients}
                        className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                      />
                    </th>
                    <th
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                      onClick={() => handleSort('displayName')}
                    >
                      <div className="flex items-center">
                        Client
                        {sortBy === 'displayName' && (
                          <span className="ml-1">
                            {sortOrder === 'asc' ? '↑' : '↓'}
                          </span>
                        )}
                      </div>
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Contact
                    </th>
                    <th
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                      onClick={() => handleSort('clientType')}
                    >
                      <div className="flex items-center">
                        Type
                        {sortBy === 'clientType' && (
                          <span className="ml-1">
                            {sortOrder === 'asc' ? '↑' : '↓'}
                          </span>
                        )}
                      </div>
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Tax IDs
                    </th>
                    <th
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                      onClick={() => handleSort('createdAt')}
                    >
                      <div className="flex items-center">
                        Created
                        {sortBy === 'createdAt' && (
                          <span className="ml-1">
                            {sortOrder === 'asc' ? '↑' : '↓'}
                          </span>
                        )}
                      </div>
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {clients.map((client) => (
                    <tr key={client.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <input
                          type="checkbox"
                          checked={selectedClients.includes(client.id)}
                          onChange={() => handleSelectClient(client.id)}
                          className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                        />
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="w-10 h-10 bg-primary-100 rounded-full flex items-center justify-center">
                            <Building2 className="w-5 h-5 text-primary-600" />
                          </div>
                          <div className="ml-4">
                            <div className="text-sm font-medium text-gray-900">{client.displayName}</div>
                            {client.legalName && client.legalName !== client.displayName && (
                              <div className="text-xs text-gray-500">Legal: {client.legalName}</div>
                            )}
                            <div className="text-sm text-gray-500 flex items-center">
                              <MapPin className="w-3 h-3 mr-1" />
                              {client.fullAddress || 'No address'}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {client.contact.email && (
                          <div className="text-sm text-gray-900 flex items-center mb-1">
                            <Mail className="w-4 h-4 mr-2 text-gray-400" />
                            {client.contact.email}
                          </div>
                        )}
                        {client.contact.phone && (
                          <div className="text-sm text-gray-500 flex items-center">
                            <Phone className="w-4 h-4 mr-2 text-gray-400" />
                            {client.contact.phone}
                          </div>
                        )}
                        {!client.contact.email && !client.contact.phone && (
                          <div className="text-sm text-gray-400">No contact info</div>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900 mb-1">
                          {getClientTypeLabel(client.clientType)}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusBadge(client.isActive)}`}>
                          {getStatusText(client.isActive)}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        <div className="space-y-1">
                          {client.taxIds.businessNumber && (
                            <div>BN: {client.taxIds.businessNumber}</div>
                          )}
                          {client.taxIds.gstNumber && (
                            <div>GST: {client.taxIds.gstNumber}</div>
                          )}
                          {client.taxIds.sin && (
                            <div>SIN: {client.taxIds.sin}</div>
                          )}
                          {!client.taxIds.businessNumber && !client.taxIds.gstNumber && !client.taxIds.sin && (
                            <div className="text-gray-400">No tax IDs</div>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        <div className="flex items-center">
                          <Calendar className="w-4 h-4 mr-2" />
                          {new Date(client.createdAt).toLocaleDateString()}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex items-center justify-end space-x-2">
                          <button
                            onClick={() => handleViewClient(client)}
                            className="text-blue-600 hover:text-blue-900"
                            title="View client"
                          >
                            <Eye className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleEditClient(client)}
                            className="text-indigo-600 hover:text-indigo-900"
                            title="Edit client"
                          >
                            <Edit className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleToggleStatus(client)}
                            className={`${client.isActive ? 'text-orange-600 hover:text-orange-900' : 'text-green-600 hover:text-green-900'}`}
                            title={client.isActive ? 'Deactivate client' : 'Activate client'}
                          >
                            {client.isActive ? <UserX className="w-4 h-4" /> : <UserCheck className="w-4 h-4" />}
                          </button>
                          <button
                            onClick={() => handleDeleteClient(client)}
                            className="text-red-600 hover:text-red-900"
                            title="Delete client"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Pagination */}
        {!isLoadingClients && !clientsError && totalClients > 0 && (
          <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
            <div className="flex-1 flex justify-between sm:hidden">
              <button
                onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                disabled={currentPage === 1}
                className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Previous
              </button>
              <button
                onClick={() => setCurrentPage(Math.min(Math.ceil(totalClients / pageSize), currentPage + 1))}
                disabled={currentPage >= Math.ceil(totalClients / pageSize)}
                className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
              </button>
            </div>
            <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
              <div>
                <p className="text-sm text-gray-700">
                  Showing{' '}
                  <span className="font-medium">{((currentPage - 1) * pageSize) + 1}</span>
                  {' '}to{' '}
                  <span className="font-medium">
                    {Math.min(currentPage * pageSize, totalClients)}
                  </span>
                  {' '}of{' '}
                  <span className="font-medium">{totalClients}</span>
                  {' '}results
                </p>
              </div>
              <div className="flex items-center space-x-2">
                <select
                  value={pageSize}
                  onChange={(e) => {
                    setPageSize(Number(e.target.value));
                    setCurrentPage(1);
                  }}
                  className="border border-gray-300 rounded-md px-3 py-1 text-sm"
                >
                  <option value={10}>10 per page</option>
                  <option value={20}>20 per page</option>
                  <option value={50}>50 per page</option>
                  <option value={100}>100 per page</option>
                </select>
                <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                  <button
                    onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                    disabled={currentPage === 1}
                    className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Previous
                  </button>
                  {/* Page numbers */}
                  {Array.from({ length: Math.min(5, Math.ceil(totalClients / pageSize)) }, (_, i) => {
                    const pageNum = i + 1;
                    return (
                      <button
                        key={pageNum}
                        onClick={() => setCurrentPage(pageNum)}
                        className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                          currentPage === pageNum
                            ? 'z-10 bg-primary-50 border-primary-500 text-primary-600'
                            : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                        }`}
                      >
                        {pageNum}
                      </button>
                    );
                  })}
                  <button
                    onClick={() => setCurrentPage(Math.min(Math.ceil(totalClients / pageSize), currentPage + 1))}
                    disabled={currentPage >= Math.ceil(totalClients / pageSize)}
                    className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Next
                  </button>
                </nav>
              </div>
            </div>
          </div>
        )}

        {/* Empty State */}
        {!isLoadingClients && !clientsError && clients.length === 0 && (
          <div className="bg-white rounded-lg border border-gray-200 text-center py-12">
            <Building2 className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No clients found</h3>
            <p className="text-gray-500 mb-4">
              {searchTerm || statusFilter !== 'all' || clientTypeFilter !== 'all'
                ? 'Try adjusting your search or filter criteria.'
                : 'Get started by adding your first client.'
              }
            </p>
            <button
              onClick={() => setShowAddModal(true)}
              className="inline-flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Client
            </button>
          </div>
        )}

        {/* Modals */}
        <ClientModal
          isOpen={showAddModal}
          onClose={() => setShowAddModal(false)}
          onSubmit={handleCreateClient}
          title="Add New Client"
          isLoading={createClientMutation.isPending}
        />

        <ClientModal
          isOpen={showEditModal}
          onClose={() => {
            setShowEditModal(false);
            setSelectedClient(null);
          }}
          onSubmit={handleUpdateClient}
          client={selectedClient}
          title="Edit Client"
          isLoading={updateClientMutation.isPending}
        />

        <ClientViewModal
          isOpen={showViewModal}
          onClose={() => {
            setShowViewModal(false);
            setSelectedClient(null);
          }}
          client={selectedClient}
          onEdit={handleViewToEdit}
        />

        <ClientImportModal
          isOpen={showImportModal}
          onClose={() => setShowImportModal(false)}
          onImport={handleImportClients}
          isLoading={importClientsMutation.isPending}
        />

        <ClientImportResultsModal
          isOpen={showImportResultsModal}
          onClose={() => {
            setShowImportResultsModal(false);
            setImportResults(null);
          }}
          results={importResults}
        />
      </div>
    </FirmAdminLayout>
  );
};
